/**
 * TẢI VIDEO LÊN KÊNH (06/10/2026, chủ dự án: «upload video lên kênh và tự động chỉnh sửa tiêu đề, mô tả, tag
 * theo mẫu») — phần tính toán thuần của phần «Tải lên»: nhận ra bộ file của một video trong thư mục tool xuất
 * ra, đọc file seo.txt, xếp lịch công khai mỗi video một ngày, viết dung lượng / tốc độ / thời gian còn lại.
 *
 * Tool dựng video (ToolNhacViet) xuất mỗi bản một thư mục:
 *   KÊNH HẢI NGOẠI XƯA 0007\  KÊNH HẢI NGOẠI XƯA 0007.mp4
 *                             KÊNH HẢI NGOẠI XƯA 0007 - seo.txt      (TIÊU ĐỀ · MÔ TẢ · TAG · CẢNH BÁO)
 *                             KÊNH HẢI NGOẠI XƯA 0007 - thumb.jpg
 *                             KÊNH HẢI NGOẠI XƯA 0007 - danh sach.txt
 * Tool dựng VIDEO NGẮN (VIDEO MV, 27/10/2026) xuất mỗi bài một thư mục, không ảnh bìa:
 *   BÀI CA KỶ NIỆM - TIỂU THÚY\  BÀI CA KỶ NIỆM - TIỂU THÚY.mp4
 *                              BÀI CA KỶ NIỆM - TIỂU THÚY - seo.txt
 *                              BÀI CA KỶ NIỆM - TIỂU THÚY - loi.ass   (lời đã dựng vào video — bỏ qua)
 */
import { splitTags } from '@halo/shared';

const VIDEO_EXTENSIONS = new Set([
  'mp4',
  'mov',
  'mkv',
  'webm',
  'avi',
  'm4v',
  'mpg',
  'mpeg',
  'wmv',
  'flv',
  '3gp',
  'ts',
]);
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);

export type UploadFileKind = 'video' | 'seo' | 'thumb' | 'other';

const extensionOf = (name: string): string => name.slice(name.lastIndexOf('.') + 1).toLowerCase();
const withoutExtension = (name: string): string =>
  name.includes('.') ? name.slice(0, name.lastIndexOf('.')) : name;

/** File này đóng vai gì trong bộ: video, seo.txt, ảnh bìa, hay thứ khác (danh sách bài, file tạm). */
export function fileKind(name: string): UploadFileKind {
  const extension = extensionOf(name);
  const stem = withoutExtension(name).toLowerCase();
  if (VIDEO_EXTENSIONS.has(extension)) return 'video';
  if (extension === 'txt' && /(^|\s-\s)seo$/.test(stem)) return 'seo';
  if (IMAGE_EXTENSIONS.has(extension)) return 'thumb';
  return 'other';
}

/** Tên gốc của bộ: «X.mp4», «X - seo.txt», «X - thumb.jpg», «X - danh sach.txt» đều là «X». */
export function stemOf(name: string): string {
  return withoutExtension(name)
    .replace(/\s-\s(seo|thumb|danh sach|danh sách|timeline|loi|lời)$/i, '')
    .trim();
}

export interface DroppedFile {
  file: File;
  /** Đường dẫn tương đối khi kéo cả thư mục («KÊNH 0007/KÊNH 0007.mp4»); '' khi chọn file rời. */
  path: string;
}

/** Bộ file của MỘT video: ít nhất phải có file video; seo.txt và ảnh bìa có thì dùng. */
export interface ReleaseFiles {
  /** Khoá bộ: thư mục chứa, hoặc tên gốc khi file rời. */
  key: string;
  name: string;
  video: File;
  seo: File | null;
  thumb: File | null;
}

/**
 * Gom file kéo vào thành từng bộ. Kéo thư mục thì mỗi thư mục (chứa file video) là một bộ; file rời thì gom
 * theo tên gốc. Thư mục có nhiều video thì mỗi video một bộ, seo/ảnh bìa ghép theo tên gốc trước, không có thì
 * lấy file lẻ loi của thư mục (thư mục một video là trường hợp thường gặp).
 */
export function groupReleases(files: readonly DroppedFile[]): ReleaseFiles[] {
  const folders = new Map<string, DroppedFile[]>();
  for (const item of files) {
    const folder = item.path.includes('/') ? item.path.slice(0, item.path.lastIndexOf('/')) : '';
    const list = folders.get(folder) ?? [];
    list.push(item);
    folders.set(folder, list);
  }
  const releases: ReleaseFiles[] = [];
  for (const [folder, items] of folders) {
    const videos = items.filter((item) => fileKind(item.file.name) === 'video');
    const seos = items.filter((item) => fileKind(item.file.name) === 'seo');
    const thumbs = items.filter((item) => fileKind(item.file.name) === 'thumb');
    const pick = (candidates: DroppedFile[], stem: string): File | null => {
      const byName = candidates.find((item) => stemOf(item.file.name) === stem);
      if (byName) return byName.file;
      return videos.length === 1 && candidates.length === 1 ? (candidates[0]?.file ?? null) : null;
    };
    for (const video of videos) {
      const stem = stemOf(video.file.name);
      releases.push({
        key: folder ? `${folder}/${video.file.name}` : video.file.name,
        name: folder && videos.length === 1 ? folder.slice(folder.lastIndexOf('/') + 1) : stem,
        video: video.file,
        seo: pick(seos, stem),
        thumb: pick(thumbs, stem),
      });
    }
  }
  return releases.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
}

export interface SeoText {
  title: string;
  description: string;
  tags: string[];
}

const SECTION = /^=+\s*(TIÊU ĐỀ|MÔ TẢ|TAG|CẢNH BÁO)\s*=+\s*$/i;
// Không dùng `\b` sau «ký tự»: chữ «ự» ngoài ASCII không tính là chữ với `\b` của JS.
const COUNT_LINE = /^\s*\d[\d.,]*(\/\d[\d.,]*)?\s*(ký tự|tag)(\s|$)/i;

/**
 * Đọc file «- seo.txt» của tool: ba khối TIÊU ĐỀ · MÔ TẢ · TAG, mỗi khối mở đầu bằng dòng đếm («100/100 ký
 * tự», «27 tag · 500/500 ký tự») rồi tới nội dung. Khối CẢNH BÁO bỏ qua. Không có khối nào thì `null`.
 */
export function parseSeoText(raw: string): SeoText | null {
  const text = raw.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n');
  const blocks: Record<string, string[]> = {};
  let current = '';
  for (const line of text.split('\n')) {
    const header = SECTION.exec(line.trim());
    if (header) {
      current = (header[1] ?? '').toUpperCase().replace(/\s+/g, ' ');
      blocks[current] = [];
      continue;
    }
    if (current) blocks[current]?.push(line);
  }
  const content = (name: string): string => {
    const lines = [...(blocks[name] ?? [])];
    // Dòng đếm ký tự ngay sau tiêu đề khối không phải nội dung.
    while (lines.length && (lines[0]?.trim() === '' || COUNT_LINE.test(lines[0] ?? ''))) {
      if (COUNT_LINE.test(lines[0] ?? '')) {
        lines.shift();
        break;
      }
      lines.shift();
    }
    return lines.join('\n').replace(/^\n+/, '').replace(/\s+$/, '');
  };
  if (!('TIÊU ĐỀ' in blocks) && !('MÔ TẢ' in blocks) && !('TAG' in blocks)) return null;
  return {
    title: content('TIÊU ĐỀ').replace(/\s+/g, ' ').trim(),
    description: content('MÔ TẢ'),
    tags: splitTags(content('TAG')),
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Giờ công khai của video thứ `index` (0 là đầu): từ `start`, mỗi video cách `stepDays` ngày (lẻ được: 0,25 = 6 giờ). */
export function scheduleSlot(start: Date, index: number, stepDays: number): Date {
  return new Date(start.getTime() + index * Math.max(0, stepDays) * DAY_MS);
}

/**
 * LOẠI VIDEO TẢI LÊN (27/10/2026, chủ dự án: «Tôi muốn thêm chức năng tải video ngắn … upload lên kênh (File MP4,
 * file seo)»): `long` = thư mục ToolNhacViet (video dài, có ảnh bìa); `short` = thư mục tool MV (một bài 4–5 phút,
 * `<tên>.mp4` + `<tên> - seo.txt` + `<tên> - loi.ass`, không ảnh bìa — YouTube tự lấy khung hình). Hai loại chung
 * hàng đợi và cách gửi; khác ở lịch (video ngắn nhiều bản một ngày → cách nhau theo GIỜ), thư mục tool riêng
 * («Đã Upload» của tool MV nằm trong thư mục MV), và không nhắc «không ảnh bìa».
 */
export type UploadKind = 'long' | 'short';
export const UPLOAD_KIND_LABELS: Record<UploadKind, string> = {
  long: 'Video dài',
  short: 'Video ngắn',
};
/** Video ngắn: mặc định cách nhau 6 giờ (4 bản một ngày), tối đa 72 giờ. */
export const SHORT_STEP_HOURS_DEFAULT = 6;
export const SHORT_STEP_HOURS_MAX = 72;
/** Khoảng cách theo giờ (ô nhập của video ngắn) ↔ theo ngày (đơn vị của `scheduleSlot`). */
export const hoursToStepDays = (hours: number): number => Math.max(0, hours) / 24;
export const stepDaysToHours = (stepDays: number): number => Math.round(Math.max(0, stepDays) * 24);

const vi = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 });

/** «6,15 GB», «521 KB». */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes >= 1024 ** 3) return `${vi.format(bytes / 1024 ** 3)} GB`;
  if (bytes >= 1024 ** 2) return `${vi.format(Math.round((bytes / 1024 ** 2) * 10) / 10)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** «12,3 MB/s». */
export function formatRate(bytesPerSecond: number): string {
  return bytesPerSecond > 0 ? `${formatBytes(bytesPerSecond)}/s` : '';
}

/** «còn 25 phút», «còn 1 giờ 05 phút», «còn 40 giây». */
export function formatEta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds < 60) return `còn ${Math.ceil(seconds)} giây`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `còn ${minutes} phút`;
  const hours = Math.floor(minutes / 60);
  return `còn ${hours} giờ ${String(minutes % 60).padStart(2, '0')} phút`;
}

/** Byte kế tiếp phải gửi theo header `Range` Google trả về khi hỏi tiến độ («bytes=0-999» → 1000). */
export function nextByteOfRange(range: string | null): number {
  const match = /(\d+)\s*$/.exec(range ?? '');
  return match ? Number(match[1]) + 1 : 0;
}

/** Mã video trong một địa chỉ YouTube hoặc chính mã ấy; '' khi không nhận ra. */
export function youtubeVideoId(text: string): string {
  const clean = text.trim();
  if (/^[\w-]{11}$/.test(clean)) return clean;
  const match = /(?:youtu\.be\/|[?&]v=|\/shorts\/|\/live\/|\/embed\/)([\w-]{11})(?![\w-])/.exec(
    clean,
  );
  return match?.[1] ?? '';
}

/** Bộ file cho một buổi PHÁT TRỰC TIẾP: chỉ cần seo.txt và / hoặc ảnh bìa; file video trong thư mục bỏ qua. */
export interface LiveSourceFiles {
  /** Tên gốc (từ seo.txt hay ảnh) — làm tiêu đề khi seo.txt không có tiêu đề. */
  name: string;
  seo: File | null;
  thumb: File | null;
}

/**
 * Chọn seo.txt và ảnh bìa trong thứ vừa kéo vào (06/10/2026, chủ dự án: «Phần tiêu đề và ảnh thum cho tôi add
 * dạng file giống như upload video»): seo.txt đầu tiên theo tên; ảnh ưu tiên cùng bộ với seo.txt (cùng tên gốc),
 * không có thì ảnh đầu tiên.
 */
export function pickLiveSource(files: readonly DroppedFile[]): LiveSourceFiles {
  const sorted = [...files].sort((a, b) => a.file.name.localeCompare(b.file.name, 'vi'));
  const seo = sorted.find((item) => fileKind(item.file.name) === 'seo')?.file ?? null;
  const images = sorted.filter((item) => fileKind(item.file.name) === 'thumb');
  const seoStem = seo ? stemOf(seo.name) : '';
  const thumb =
    (seoStem ? images.find((item) => stemOf(item.file.name) === seoStem) : undefined)?.file ??
    images[0]?.file ??
    null;
  return { name: seo ? stemOf(seo.name) : thumb ? stemOf(thumb.name) : '', seo, thumb };
}

// ---------------------------------------------------------------- tiến độ ngoài màn hình

/** Phần của một video trong hàng đợi mà thanh tiến độ ngoài màn cần biết. */
export interface QueuedUploadView {
  status: string;
  name: string;
  size: number;
  sent: number;
  rate: number;
}

export interface UploadQueueSummary {
  /** Video đang gửi (hay đang hoàn tất); null = không có gì đang chạy. */
  current: {
    name: string;
    /** 0 → 100, số nguyên; đang hoàn tất = 100. */
    percent: number;
    rate: number;
    /** Giây còn lại theo tốc độ hiện tại; 0 khi chưa đo được. */
    etaSeconds: number;
    finishing: boolean;
  } | null;
  /** Video còn chờ tới lượt. */
  waiting: number;
}

/**
 * Tóm tắt hàng đợi cho thanh tiến độ ngoài màn «Đăng video» (07/10/2026, chủ dự án: «chuyển sang chức năng
 * khác … giữ % để tôi theo dõi đến hoàn thành»): video đang gửi, phần trăm, tốc độ, còn bao lâu, còn chờ mấy.
 */
export function uploadQueueSummary(items: readonly QueuedUploadView[]): UploadQueueSummary {
  const active = items.filter((item) => item.status === 'uploading' || item.status === 'finishing');
  const waiting = items.filter((item) => item.status === 'ready').length;
  const [first] = active;
  if (!first) return { current: null, waiting };
  // Gửi nhiều video cùng lúc (09/10/2026): cộng dồn cỡ, phần đã gửi và tốc độ của mọi video đang gửi.
  const finishing = active.every((item) => item.status === 'finishing');
  const size = active.reduce((sum, item) => sum + item.size, 0);
  const sent = active.reduce(
    (sum, item) => sum + (item.status === 'finishing' ? item.size : item.sent),
    0,
  );
  const rate = active.reduce((sum, item) => sum + (item.status === 'finishing' ? 0 : item.rate), 0);
  const percent = finishing ? 100 : size > 0 ? Math.min(100, Math.floor((sent / size) * 100)) : 0;
  const etaSeconds = !finishing && rate > 0 ? Math.max(0, size - sent) / rate : 0;
  return {
    current: {
      name: active.length > 1 ? `${active.length} video cùng lúc` : first.name,
      percent,
      rate,
      etaSeconds,
      finishing,
    },
    waiting,
  };
}

/** Đang đứng ở tab Tải lên của Đăng video (không có `tab` = tab đầu): bảng ở đó đã hiện tiến độ từng video. */
export function isUploadTabLocation(pathname: string, search: string): boolean {
  if (pathname !== '/video-publish') return false;
  const tab = new URLSearchParams(search).get('tab');
  return tab === null || tab === '' || tab === 'upload' || tab === 'queue';
}

/** Phần trăm đã gửi của một video trong hàng đợi (0 → 100): đã lên / đang hoàn tất = 100. */
export function uploadPercent(item: { status: string; size: number; sent: number }): number {
  if (item.status === 'done' || item.status === 'finishing') return 100;
  if (!(item.size > 0)) return 0;
  return Math.min(100, Math.floor((item.sent / item.size) * 100));
}

// ---------------------------------------------------------------- chọn vài thư mục mỗi lần (09/10/2026)

/** Mỗi lần chọn mặc định tích chừng này video (chủ dự án: «cho tôi chọn lần 3-5 foder»). */
export const RELEASE_PICK_DEFAULT = 5;

/** Bộ đã có trong hàng đợi (cùng tên file video và cỡ file) — không tích sẵn, tránh tải trùng. */
export const releaseQueueKey = (fileName: string, size: number): string => `${fileName}|${size}`;

/** Khoá các bộ tích sẵn khi mở hộp chọn: bộ chưa có trong hàng đợi, theo thứ tự tên, tối đa `limit`. */
export function defaultReleasePicks(
  releases: readonly ReleaseFiles[],
  queued: ReadonlySet<string>,
  limit = RELEASE_PICK_DEFAULT,
): string[] {
  return releases
    .filter((release) => !queued.has(releaseQueueKey(release.video.name, release.video.size)))
    .slice(0, limit)
    .map((release) => release.key);
}
